import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Clock,
  Users,
  Calendar,
  User,
  FileText,
  Image,
  Video,
  Download,
  CheckCircle,
  ArrowLeft,
  BookOpen,
  Play,
} from "lucide-react";

const API = `${window.location.origin}/api`;

const videoZones = [
  { key: "video_viso", label: "Viso", icon: "👤" },
  { key: "video_corpo", label: "Corpo", icon: "🧍" },
  { key: "video_braccia_mani", label: "Braccia e Mani", icon: "🤲" },
  { key: "video_gambe_piedi", label: "Gambe e Piedi", icon: "🦶" },
  { key: "video_addome", label: "Addome", icon: "⭕" },
  { key: "video_testa_collo", label: "Testa e Collo", icon: "🗣️" },
];

const getLevelBadgeClass = (level) => {
  switch (level) {
    case "Base":
      return "badge-base";
    case "Intermedio":
      return "badge-intermedio";
    case "Avanzato":
      return "badge-avanzato";
    default:
      return "";
  }
};

const getFileIcon = (type) => {
  switch (type) {
    case "pdf":
      return <FileText className="w-5 h-5 text-red-500" />;
    case "image":
      return <Image className="w-5 h-5 text-blue-500" />;
    case "video":
      return <Video className="w-5 h-5 text-purple-500" />;
    default:
      return <FileText className="w-5 h-5" />;
  }
};

const CourseDetailPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [booking, setBooking] = useState(false);
  const [alreadyBooked, setAlreadyBooked] = useState(false);

  useEffect(() => {
    fetchCourse();
    checkExistingBooking();
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`);
      setCourse(response.data);
    } catch (error) {
      toast.error("Corso non trovato");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const checkExistingBooking = async () => {
    try {
      const response = await axios.get(`${API}/bookings/my`);
      const existingBooking = response.data.find(
        (b) => b.course_id === courseId && b.status === "confirmed"
      );
      setAlreadyBooked(!!existingBooking);
    } catch (error) {
      console.error("Error checking bookings:", error);
    }
  };

  const handleBooking = async () => {
    if (!privacyAccepted) {
      toast.error("È necessario accettare la privacy policy");
      return;
    }

    setBooking(true);
    try {
      await axios.post(`${API}/bookings`, {
        course_id: courseId,
        privacy_accepted: true,
      });
      toast.success("Iscrizione completata con successo! Riceverai una email di conferma.");
      setBookingDialogOpen(false);
      setAlreadyBooked(true);
      fetchCourse();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'iscrizione");
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-secondary rounded w-1/3" />
            <div className="h-64 bg-secondary rounded-2xl" />
            <div className="h-32 bg-secondary rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!course) return null;

  const spotsLeft = course.max_participants - course.current_participants;
  const isFull = spotsLeft <= 0;

  return (
    <PageLayout>
      

      {/* Hero */}
      <div className="relative h-64 md:h-80 overflow-hidden bg-secondary">
        <img
          src={course.image_url || "https://images.unsplash.com/photo-1737063935340-f9af0940c4c5?crop=entropy&cs=srgb&fm=jpg&q=85"}
          alt={course.title}
          className="w-full h-full object-cover object-center"
          onError={(e) => {
            e.target.src = "https://images.unsplash.com/photo-1737063935340-f9af0940c4c5?crop=entropy&cs=srgb&fm=jpg&q=85";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <div className="max-w-7xl mx-auto">
            <Button
              variant="ghost"
              className="text-white mb-4 rounded-full hover:bg-white/20"
              onClick={() => navigate("/")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna ai corsi
            </Button>
            <div className="flex flex-wrap gap-2 mb-3">
              <Badge className={`${getLevelBadgeClass(course.level)} border-0`}>
                {course.level}
              </Badge>
              <Badge variant="secondary" className="rounded-full">
                {course.product_line}
              </Badge>
            </div>
            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-white">
              {course.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <Card className="rounded-2xl border-border/50">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  Descrizione del corso
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">{course.description}</p>
              </CardContent>
            </Card>

            {/* Modules */}
            <Card className="rounded-2xl border-border/50">
              <CardHeader>
                <CardTitle className="font-serif text-xl">Contenuti del corso</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {course.modules.map((module, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-foreground">{module}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Materials */}
            {course.materials && course.materials.length > 0 && (
              <Card className="rounded-2xl border-border/50">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Materiali scaricabili</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {course.materials.map((material) => (
                      <a
                        key={material.material_id}
                        href={material.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors group"
                        data-testid={`material-${material.material_id}`}
                      >
                        {getFileIcon(material.file_type)}
                        <span className="flex-1 font-medium">{material.name}</span>
                        <Download className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Video Esempi */}
            {videoZones.some(zone => course[zone.key]) && (
              <Card className="rounded-2xl border-border/50">
                <CardHeader>
                  <CardTitle className="font-serif text-xl flex items-center gap-2">
                    <Play className="w-5 h-5 text-primary" />
                    Video Esempi per Zone del Corpo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {videoZones.map((zone) => (
                      course[zone.key] && (
                        <div key={zone.key} className="space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium">
                            <span>{zone.icon}</span>
                            <span>{zone.label}</span>
                          </div>
                          <video
                            src={course[zone.key]}
                            className="w-full h-32 object-cover rounded-xl bg-black"
                            controls
                            preload="metadata"
                          />
                        </div>
                      )
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Instructor Notes */}
            {course.instructor_notes && (
              <Card className="rounded-2xl border-border/50 bg-secondary/30">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Note del docente</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground italic">{course.instructor_notes}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Booking Card */}
            <Card className="rounded-2xl border-border/50 sticky top-24">
              <CardContent className="p-6">
                {/* Course Info */}
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Data</p>
                      <p className="font-medium">
                        {new Date(course.date).toLocaleDateString("it-IT", {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Orario</p>
                      <p className="font-medium">{course.schedule} ({course.duration})</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Docente</p>
                      <p className="font-medium">{course.instructor}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Posti disponibili</p>
                      <p className={`font-medium ${isFull ? "text-destructive" : ""}`}>
                        {isFull ? "Corso al completo" : `${spotsLeft} di ${course.max_participants}`}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                {alreadyBooked ? (
                  <div className="text-center p-4 bg-green-50 rounded-xl border border-green-200">
                    <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="font-medium text-green-800">Sei già iscritto!</p>
                    <p className="text-sm text-green-600">Controlla il tuo calendario</p>
                  </div>
                ) : (
                  <Button
                    className="w-full rounded-full btn-shine py-6"
                    disabled={isFull}
                    onClick={() => setBookingDialogOpen(true)}
                    data-testid="booking-btn"
                  >
                    {isFull ? "Corso al completo" : "Iscriviti al corso"}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Conferma Iscrizione</DialogTitle>
            <DialogDescription>
              Stai per iscriverti al corso "{course.title}"
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-secondary/50 rounded-xl p-4 mb-4">
              <p className="font-medium mb-2">{course.title}</p>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Data: {new Date(course.date).toLocaleDateString("it-IT")}</p>
                <p>Ora: {course.time}</p>
                <p>Docente: {course.instructor}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="privacy"
                checked={privacyAccepted}
                onCheckedChange={setPrivacyAccepted}
                data-testid="privacy-checkbox"
              />
              <Label htmlFor="privacy" className="text-sm leading-relaxed cursor-pointer">
                Accetto l'informativa sulla privacy e autorizzo il trattamento dei miei dati personali per la gestione dell'iscrizione al corso.
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => setBookingDialogOpen(false)}
            >
              Annulla
            </Button>
            <Button
              className="rounded-full btn-shine"
              onClick={handleBooking}
              disabled={booking || !privacyAccepted}
              data-testid="confirm-booking-btn"
            >
              {booking ? "Iscrizione in corso..." : "Conferma Iscrizione"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default CourseDetailPage;
